<template>
    <div :aria-hidden="showReconnect" :inert="showReconnect" v-show="!showReconnect">
        <slot />
    </div>
    <ReconnectScreen v-if="showReconnect" />
</template>

<script setup lang="ts">
import ReconnectScreen from '@/Components/ReconnectScreen.vue';
import { useSessionStorage } from '@/composables/useSessionStorage';
import { useConnectivityStore } from '@/stores/connectivity';
import * as Sentry from '@sentry/vue';
import { storeToRefs } from 'pinia';
import { computed, onBeforeUnmount, onMounted, watch } from 'vue';

const READY_WATCHDOG_MS = 30_000;
const READY_WATCHDOG_RELOAD_KEY = 'connectivity_watchdog_reloads';
const READY_WATCHDOG_MAX_RELOADS = 3;

const connectivity = useConnectivityStore();
const { state, hasEverConnected } = storeToRefs(connectivity);
const sessionStore = useSessionStorage();

const showReconnect = computed(() => !hasEverConnected.value && (state.value === 'offline' || state.value === 'retrying'));

let watchdogTimer: ReturnType<typeof setTimeout> | null = null;
let stopReadyWatcher: (() => void) | null = null;

function clearWatchdog() {
    if (watchdogTimer !== null) {
        clearTimeout(watchdogTimer);
        watchdogTimer = null;
    }
}

function triggerWatchdogReload() {
    const previous = Number(sessionStore.getItem(READY_WATCHDOG_RELOAD_KEY) ?? '0') || 0;

    if (previous >= READY_WATCHDOG_MAX_RELOADS) {
        Sentry.addBreadcrumb({
            category: 'connectivity',
            message: `Boot watchdog skipped: reload cap (${READY_WATCHDOG_MAX_RELOADS}) reached`,
            level: 'warning',
        });
        return;
    }

    sessionStore.setItem(READY_WATCHDOG_RELOAD_KEY, String(previous + 1));

    Sentry.addBreadcrumb({
        category: 'connectivity',
        message: `Boot watchdog firing reload (attempt ${previous + 1}) after ${READY_WATCHDOG_MS}ms without READY`,
        level: 'warning',
    });

    window.location.reload();
}

onMounted(() => {
    connectivity.start();

    if (state.value === 'online') {
        sessionStore.removeItem(READY_WATCHDOG_RELOAD_KEY);
        return;
    }

    watchdogTimer = setTimeout(() => {
        watchdogTimer = null;
        if (state.value === 'online') {
            return;
        }
        triggerWatchdogReload();
    }, READY_WATCHDOG_MS);

    stopReadyWatcher = watch(state, (current) => {
        if (current === 'online') {
            clearWatchdog();
            sessionStore.removeItem(READY_WATCHDOG_RELOAD_KEY);
            stopReadyWatcher?.();
            stopReadyWatcher = null;
        }
    });
});

onBeforeUnmount(() => {
    clearWatchdog();
    stopReadyWatcher?.();
    stopReadyWatcher = null;
    connectivity.dispose();
});
</script>
